import { BaseAsyncValue } from '@ordersnproducts/modules/shared/models/async-value';
import { filter, map, take } from 'rxjs/operators';
import { getPumpPrimerState } from '@ordersnproducts/modules/shared/modules/pump-primer/redux/pump-primer.selectors';
import { combineLatest, Observable } from 'rxjs';
import {
  getRmsRecipeSpecifications,
  getRmsSpecificationFormState,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { getOpenPlatformContracts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/contracts.selectors';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import {
  isOrderWithPumpPrimerProduct,
  isUnloadTypePumpValue,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/pump-primer-helper';
import { PUMP_NONE_CODE } from 'app/shared/pumping/pumping.constant';

export namespace PumpPrimerProductFieldProvider {
  /**
   * Service to hide or show the pump primer product field(pump primer mix) based on the unload type selected
   */
  export class ValidateUnloadTypeField extends BaseAsyncValue<boolean> {
    getValue(): Observable<boolean> {
      const getPumpPrimerState$ = this.store.select(getPumpPrimerState);
      const getRmsSpecificationForms$ = this.store.select(getRmsSpecificationFormState);
      return combineLatest([getPumpPrimerState$, getRmsSpecificationForms$]).pipe(
        map(([_pumpPrimerState, _state]) => {
          const id = this.form.get('id').value;
          const recipe = _state.forms.find((x) => x.id === id);
          const index = _state.forms.indexOf(recipe);
          const unloadTypeFormValue = _safe(() => _state.forms[0].values.unloadType, undefined);
          const unloadTypePumpValues = _pumpPrimerState.unloadTypePumpValues;
          const isNotPumpPrimer = !(isUnloadTypePumpValue(unloadTypeFormValue, unloadTypePumpValues) && index === 0);
          return isNotPumpPrimer;
        }),
      );
    }
  }

  export class ValidatePumpPrimerProductField extends BaseAsyncValue<boolean> {
    getValue(): Observable<boolean> {
      const nameField = this.config.name;
      const formControl = this.form.get(nameField);
      const getPumpPrimerState$ = this.store.select(getPumpPrimerState);
      const getRmsSpecificationForms$ = this.store.select(getRmsSpecificationFormState);
      return combineLatest([getPumpPrimerState$, getRmsSpecificationForms$]).pipe(
        filter(([_pumpPrimerState, _state]) => _state.forms.length > 0),
        map(([_pumpPrimerState, _state]) => {
          const id = this.form.get('id').value;
          const recipe = _safe(() => _state.forms.find((x) => x.id === id), undefined);
          const index = _safe(() => _state.forms.indexOf(recipe), undefined);
          const { pumpPrimerProduct: pumpPrimerMix, unloadType } = _safe(() => _state.forms[0].values, undefined);
          const unloadTypePumpValues = _pumpPrimerState.unloadTypePumpValues;
          const hide = isOrderWithPumpPrimerProduct(pumpPrimerMix, unloadType, unloadTypePumpValues)
            ? index !== 0
            : true;
          if (hide) {
            formControl.disable({ emitEvent: false });
          } else {
            formControl.enable({ emitEvent: false });
          }
          return hide;
        }),
      );
    }
  }

  export class ValidateInAddProduct extends BaseAsyncValue<boolean> {
    getValue(): Observable<boolean> {
      return this.store.select(getRmsSpecificationFormState).pipe(
        map(({ forms }) => {
          const id = this.form.get('id').value;
          const recipe = forms.find((x) => x.id === id);
          const index = forms.indexOf(recipe);
          return index !== 0;
        }),
      );
    }
  }

  export class DefaultPumpPrimerQuantity extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      const selectedPrimerQuantity$ = this.getSelectedQuantity().pipe(take(1));
      const defaultPrimerQuantity$ = this.getDefaultPrimerQuantity().pipe(take(1));

      return combineLatest([selectedPrimerQuantity$, defaultPrimerQuantity$]).pipe(
        map(([selectedPrimerQuantity, defaultPrimerQuantity]) => {
          if (selectedPrimerQuantity === null || isNaN(selectedPrimerQuantity || !selectedPrimerQuantity)) {
            return defaultPrimerQuantity;
          } else {
            return selectedPrimerQuantity;
          }
        }),
      );
    }

    private getSelectedQuantity() {
      return this.store.select(getRmsRecipeSpecifications).pipe(
        map((recipe) => {
          return recipe[0].values.primerQuantity ? recipe[0].values.primerQuantity : null;
        }),
      );
    }

    private getDefaultPrimerQuantity() {
      return this.store
        .select(getPumpPrimerState)
        .pipe(map(({ defaultPumpPrimerQuantity }) => defaultPumpPrimerQuantity));
    }
  }

  export class MinPumpPrimerQuantity extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      return this.store.select(getPumpPrimerState).pipe(
        map(({ minPumpPrimerQuantity }) => minPumpPrimerQuantity),
        take(1),
      );
    }
  }

  export class MaxPumpPrimerQuantity extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      return this.store.select(getPumpPrimerState).pipe(
        map(({ maxPumpPrimerQuantity }) => maxPumpPrimerQuantity),
        take(1),
      );
    }
  }

  export class Exists extends BaseAsyncValue<string> {
    getValue(): Observable<string> {
      const featureToggle: FeatureToggleService = this.injector.get(FeatureToggleService);
      return combineLatest([
        this.store.select(getRmsRecipeSpecifications),
        this.store.select(getOpenPlatformContracts),
      ]).pipe(
        map(([, availableContracts]) => {
          return availableContracts;
        }),
        map((availableContracts) => {
          if (!featureToggle.isFeatureEnabled(Features.ORDER_MISSING_FIELDS_ALERT)) {
            return this.form.get(this.config.name).status;
          }
          const productCode = this.form.get(SpecFieldNames.PRODUCT_SELECTION).value;
          const contractCode = this.form.get(SpecFieldNames.CONTRACT_SELECT).value;
          const contracts = availableContracts[productCode];
          const contract = _safe(() =>
            contracts.find((localContract) => localContract.contractCode.replace(/^0+/, '') === contractCode),
          ) as any;
          const pumpPrimerMix = this.form.get(this.config.name);
          pumpPrimerMix.setErrors(null);
          if (
            (!contract ||
              !_safe(() =>
                contract.pumpPrimerProducts.find((ppProd) => ppProd.pumpPrimerCode === pumpPrimerMix.value),
              )) &&
            pumpPrimerMix.value !== PUMP_NONE_CODE
          ) {
            pumpPrimerMix.setErrors({ notExists: true });
            return 'error';
          }

          return pumpPrimerMix ? pumpPrimerMix.status : '';
        }),
      );
    }
  }
}
